import { useCallback, useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import QueueTab from "@/pages/admin/portal/QueueTab";
import ConnectionsTab from "@/pages/admin/portal/ConnectionsTab";
import RequestTypesTab from "@/pages/admin/portal/RequestTypesTab";
import PhotosTab from "@/pages/admin/portal/PhotosTab";

export default function PortalControls() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "queue";
  const [queue, setQueue] = useState(null);
  const [denied, setDenied] = useState(false);

  const loadQueue = useCallback(async () => {
    try {
      const { data } = await api.get("/portal/queue");
      setQueue(data);
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
      else toast.error(apiError(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  if (denied)
    return (
      <div data-testid="portal-denied">
        <h1 className="text-4xl font-bold tracking-tight">Portal controls</h1>
        <p className="mt-4 text-sm font-semibold text-[#EF4444]">
          You do not have permission to view family portal controls.
        </p>
      </div>
    );

  const c = queue?.counts || {};

  return (
    <div data-testid="portal-controls">
      <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Family Portal</p>
      <h1 className="mt-3 text-4xl font-bold tracking-tight">Portal controls</h1>
      <p className="mt-3 text-muted-foreground max-w-3xl">
        Everything waiting on a director: family requests to decide, who is connected to which child, which
        requests families may make, and photos held back for review.
      </p>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="portal-counters">
        {[
          ["Open requests", c.open ?? 0, "queue-count-open"],
          [`Waiting over ${queue?.stale_after_days ?? 3} days`, c.stale ?? 0, "queue-count-stale"],
          ["Photos held", c.held_photos ?? 0, "queue-count-photos"],
          ["Photo consent to set", c.pending_consent ?? 0, "queue-count-consent"],
        ].map(([label, value, testid]) => (
          <div key={testid} className="bg-white rounded-2xl border border-border p-4" data-testid={testid}>
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
            <p className="mt-2 font-display text-3xl font-bold">{value}</p>
          </div>
        ))}
      </div>

      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })} className="mt-8">
        <TabsList data-testid="portal-tabs">
          <TabsTrigger value="queue" data-testid="tab-queue">Requests</TabsTrigger>
          <TabsTrigger value="connections" data-testid="tab-connections">Connections</TabsTrigger>
          <TabsTrigger value="types" data-testid="tab-types">Request types</TabsTrigger>
          <TabsTrigger value="photos" data-testid="tab-photos">Photo approvals</TabsTrigger>
        </TabsList>
        <TabsContent value="queue" className="mt-6">
          <QueueTab queue={queue} reload={loadQueue} />
        </TabsContent>
        <TabsContent value="connections" className="mt-6"><ConnectionsTab /></TabsContent>
        <TabsContent value="types" className="mt-6"><RequestTypesTab /></TabsContent>
        <TabsContent value="photos" className="mt-6"><PhotosTab reload={loadQueue} /></TabsContent>
      </Tabs>
    </div>
  );
}
